import { useState, useEffect } from "react";

import { HttpStatusCode } from "axios";
import { useNavigate } from "react-router-dom";
import { useDisclosure, Input, Select, Button, useToast } from "@chakra-ui/react";

import AddIcon from "@/assets/AddIcon";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST, COLORS } from "@/common/constants";
import ModalDelete from "@/components/Modals/ModalDelete";
import TableInvoiceTemplate from "@/components/Tables/TableInvoiceTemplate";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { downloadFile } from "@/services/downloadService";
import { deleteInvoiceTemplate, getListInvoiceTemplate } from "@/services/invoiceTemplateService";

import '../../styles/ListPage.css';

const InvoiceTemplateList = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const name = useAppSelector((state: RootState) => state.account.name);
    const toast = useToast();

    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [dataList, setDataList] = useState([]) as any;
    const [hasNextPage, setHasNextPage] = useState(false);
    const [id, setId] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [searchValue, setSearchValue] = useState('');
    const [statusValue, setStatusValue] = useState('');
    const [totalPageNumber, setTotalPageNumber] = useState(1);

    const handleSubmitSearch = () => {
        getData();
    }

    const handleEdit = (id: string) => {
        navigate('/settings/template/edit', { state: { id } });
    }

    const handleDelete = (id: string) => {
        setId(id);
        onOpen();
    }

    const handleConfirmDelete = async () => {
        const obj = {
            invoiceTemplateId: id,
            modifiedBy: name || "System"
        }

        const response = await deleteInvoiceTemplate(obj) as any;
        if (response.status === HttpStatusCode.Ok) {
            getData();
            toast({
                title: 'Template deleted.',
                description: "We've deleted template for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        } else {
            toast({
                title: 'Template failed to delete.',
                description: "We failed to delete template for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }

        onClose();
    }

	const handleDownload = async (idFile: string) => {
		await downloadFile(idFile);
    }

    const getData = async () => {
        const param = {
            search: searchValue,
            pageNumber: currentPageNumber,
            pageSize: currentPageSize,
            status: statusValue
        };

        const response = await getListInvoiceTemplate(param) as any;
        if (response.status === HttpStatusCode.Ok) {
            setDataList(response.data);
            setTotalPageNumber(response.totalPage);
            // setCurrentPageNumber(response.pageNumber);
            setHasNextPage(response.hasNextPage);
        } else {
            toast({
                title: response.error.code,
                description: response.error.message,
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }

        setIsLoading(false);
    }

    useEffect(() => {
        getData();
    }, []);

    useEffect(() => {
        getData();
    }, [currentPageSize, currentPageNumber]);

    return (
        <>
            <ModalDelete title="Delete it?" isOpen={isOpen} onClose={onClose} handleConfirmDelete={handleConfirmDelete} />
            <div className="table-container">
                <div className="table-filter-action-container">
                    <div className="table-filter-container">
                        <div className="table-filter-input filter-input-width-9">
                            <Input placeholder='Search' onChange={(e: any) => setSearchValue(e.target.value)} />
                        </div>
                        <div className="table-filter-input filter-input-width-5">
                            <Select placeholder='All Status' onChange={(e: any) => setStatusValue(e.target.value)}>
                                <option key="active" value="true">Active</option>
                                <option key="inactive" value="false">Inactive</option>
                            </Select>
                        </div>
                        <div>
                            <Button className='table-search-button' bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND} variant='solid' borderRadius={10} onClick={handleSubmitSearch}>
                                Search
                            </Button>
                        </div>
                        <div className="table-filter-separator"/>
                        <Button leftIcon={<AddIcon />} className='table-add-button' bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND} variant='solid' borderRadius={10} onClick={() => navigate('/settings/template/add')}>
                            Create Template
                        </Button>
                    </div>
                </div>
                <TableInvoiceTemplate dataList={dataList} isLoading={isLoading} handleEdit={handleEdit} handleDelete={handleDelete} currentPageSize={currentPageSize} handleChangePageSize={setCurrentPageSize} handleChangePageNumber={setCurrentPageNumber} currentPageNumber={currentPageNumber} totalPageNumber={totalPageNumber} hasNextPage={hasNextPage} handleDownload={handleDownload} />
            </div>
        </>
    )
}

export default InvoiceTemplateList;
